import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Formik } from 'formik';
import { InputTextField } from '@/components/UI/InputTextField';
import { BankAccountNumberField } from '@/kit/form/input/BankAccountNumberField';
import { BankStatementUploadField } from '@/kit/form/BankStatementUploadField';
import { BaseSelectField } from '@/kit/form/select/BaseSelect';
import { RoundButton } from '@/components/UI/RoundButton';
import { RoundLink } from '@/components/UI/RoundLink';
import { ACCOUNT_TYPES } from '@/helpers/constants';
import { SepaZoneCountrySelect } from './SepaZoneCountrySelect';
import { getFlagURL } from '@/helpers/utils';
import { APP_LINKS } from '@/helpers/links';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { CURRENCY_CODES as CURRENCY_CODES_T } from '@/helpers/currency';
import { VALIDATION_SCHEMA } from './schema';
import s from './index.module.scss';

const ACCOUNT_TYPE_LABEL_T = t => ({
  [ACCOUNT_TYPES.business]: t('bank_details_form.business'),
  [ACCOUNT_TYPES.personal]: t('bank_details_form.personal'),
});

export const BankDetailForm = ({ data, onSubmit }) => {
  const { isCombinedMode, isSplitMode, isLender } = useGetMe();
  const { t } = useTranslation('bank-accounts');
  const { t: tCommon } = useTranslation('common');
  const ACCOUNT_TYPE_LABEL = ACCOUNT_TYPE_LABEL_T(t);
  const CURRENCY_CODES = CURRENCY_CODES_T(tCommon);

  const currencyOptions = CURRENCY_CODES.map(item => ({
    value: item.value,
    label: `${item.label} (${item.value})`,
  }));

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={VALIDATION_SCHEMA(t)}
      initialValues={{
        id: data?.id,
        country: data
          ? {
              value: data.country_code,
              code: data.country_code,
              label: data.country,
              flag: getFlagURL(data.country_code),
            }
          : null,
        in_sepa_zone: !!data?.is_sepa_zone,
        is_valid_sepa: false,
        files: data?.bank_statement ? [data.bank_statement] : [],
        beneficiary_name: data?.beneficiary_name || '',
        account_number: data?.account_number || '',
        account_type: data?.main_account_type
          ? { label: ACCOUNT_TYPE_LABEL[data.main_account_type], value: data.main_account_type }
          : isSplitMode
            ? isLender
              ? {
                  label: ACCOUNT_TYPE_LABEL[ACCOUNT_TYPES.personal],
                  value: ACCOUNT_TYPES.personal,
                }
              : {
                  label: ACCOUNT_TYPE_LABEL[ACCOUNT_TYPES.business],
                  value: ACCOUNT_TYPES.business,
                }
            : {
                label: ACCOUNT_TYPE_LABEL[ACCOUNT_TYPES.personal],
                value: ACCOUNT_TYPES.personal,
              },
        account_currency: data?.account_currency
          ? CURRENCY_CODES.find(i => i.value === data.account_currency) || null
          : null,
        bank_statement: data?.bank_statement?.id,
        swift: data?.swift || '',
        beneficiary_address: data?.beneficiary_address || '',
        beneficiary_bank: data?.beneficiary_bank || '',
        beneficiary_bank_address: data?.beneficiary_bank_address || '',
      }}
    >
      {({
        values,
        errors,
        touched,
        dirty,
        setValues,
        handleBlur,
        handleChange,
        setFieldError,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className={s.form}>
          <h1 className="f-26 f-500">{t('bank_details_form.bank_account_info')}</h1>
          {data?.review_status === 'rejected' && (
            <div className={classnames(s.reason, 'f-14')}>{data.rejected_reason}</div>
          )}

          <div className={s.inner}>
            {isCombinedMode && (
              <BaseSelectField
                name="account_type"
                value={values.account_type}
                options={Object.keys(ACCOUNT_TYPES).map(key => ({
                  label: ACCOUNT_TYPE_LABEL[key],
                  value: key,
                }))}
                onChange={option => setFieldValue('account_type', option)}
                label={t('bank_details_form.account_type', { required: '*' })}
                error={errors.account_type && dirty && errors.account_type}
              />
            )}
            <div>
              <BaseSelectField
                name="account_currency"
                value={values.account_currency}
                options={currencyOptions}
                onChange={option => setFieldValue('account_currency', option)}
                label={t('bank_details_form.account_currency', { required: '*' })}
                error={errors.account_currency && dirty && errors.account_currency}
              />
              {values.account_currency &&
                !errors.account_currency &&
                values.account_currency.value !== 'EUR' && (
                  <div className={s.currency_non_eur}>
                    {t('bank_details_form.withdrawals_to_non_eur_fees')}
                  </div>
                )}
            </div>
            <SepaZoneCountrySelect
              value={values.country}
              onChange={data => setValues({ ...values, ...data })}
              label={t('bank_details_form.select_your_bank_country', { required: '*' })}
              error={errors.country && dirty && errors.country}
            />

            <InputTextField
              onChange={handleChange}
              name="beneficiary_name"
              label={t('bank_details_form.beneficiary_name', { required: '*' })}
              value={values.beneficiary_name}
              onBlur={handleBlur}
              error={errors.beneficiary_name && touched.beneficiary_name && errors.beneficiary_name}
            />

            {!values.in_sepa_zone && (
              <>
                <InputTextField
                  onChange={handleChange}
                  name="swift"
                  label={t('bank_details_form.swift_bic_code', { required: '*' })}
                  value={values.swift}
                  onBlur={handleBlur}
                  error={errors.swift && touched.swift && errors.swift}
                />

                <InputTextField
                  onChange={handleChange}
                  name="beneficiary_address"
                  label={t('bank_details_form.address', { required: '*' })}
                  value={values.beneficiary_address}
                  onBlur={handleBlur}
                  error={
                    errors.beneficiary_address &&
                    touched.beneficiary_address &&
                    errors.beneficiary_address
                  }
                />

                <InputTextField
                  onChange={handleChange}
                  name="beneficiary_bank"
                  label={t('bank_details_form.bank_name', { required: '*' })}
                  value={values.beneficiary_bank}
                  onBlur={handleBlur}
                  error={
                    errors.beneficiary_bank && touched.beneficiary_bank && errors.beneficiary_bank
                  }
                />

                <InputTextField
                  onChange={handleChange}
                  name="beneficiary_bank_address"
                  label={t('bank_details_form.bank_address', { required: '*' })}
                  value={values.beneficiary_bank_address}
                  onBlur={handleBlur}
                  error={
                    errors.beneficiary_bank_address &&
                    touched.beneficiary_bank_address &&
                    errors.beneficiary_bank_address
                  }
                />
              </>
            )}

            <BankAccountNumberField
              onChange={handleChange}
              name="account_number"
              value={values.account_number}
              showSepa={values.in_sepa_zone}
              label={t('bank_details_form.bank_account_number', { required: '*' })}
              countryCode={values.country?.code}
              error={errors.account_number || values.is_valid_sepa}
              onValidate={({ is_valid }) =>
                setFieldValue(
                  'is_valid_sepa',
                  is_valid ? '' : t('bank_details_form.incorrect_iban_format'),
                )
              }
            />
            <BankStatementUploadField
              name="files"
              value={values.files}
              error={errors.files && touched.files && errors.files}
              onBlur={handleBlur}
              onChange={files => setFieldValue('files', files)}
              onError={message => setFieldError('files', message)}
              onRemove={() => setFieldValue('files', [])}
            />
          </div>
          <div className={s.controls}>
            <RoundLink
              label={t('bank_details_form.back')}
              className="hov"
              path={APP_LINKS.bankAccounts}
            />
            <RoundButton
              type="submit"
              label={data ? t('bank_details_form.save') : t('bank_details_form.submit')}
              className="hov"
              disabled={!dirty || Object.keys(errors).length || isSubmitting}
              fillBackground
            />
          </div>
        </form>
      )}
    </Formik>
  );
};
